import { useMemo, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useConfig } from '../../context/ConfigContext';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function CustomTooltip({ active, payload, formatAmount }) {
  if (!active || !payload?.length) return null;
  const fullLabel = payload[0]?.payload?.fullLabel || payload[0]?.payload?.label;

  return (
    <div className="bg-white border border-surface-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-medium text-surface-700 mb-1">{fullLabel}</p>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="font-medium text-surface-900">{formatAmount(entry.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function MonthlyTrendChart({ data, type = 'income', combined = false, granularity = 'monthly', compact = false }) {
  const { formatAmount, config } = useConfig();

  const incomeColor = config.incomeColor || '#10b981';
  const expenseColor = config.expenseColor || '#ef4444';
  const color = type === 'income' ? incomeColor : expenseColor;

  const [showIncome, setShowIncome] = useState(true);
  const [showExpense, setShowExpense] = useState(true);

  const gradientId = `${type}${compact ? 'Compact' : 'Full'}Gradient`;
  const incomeGradientId = `income${compact ? 'Compact' : 'Full'}CombinedGradient`;
  const expenseGradientId = `expense${compact ? 'Compact' : 'Full'}CombinedGradient`;

  // Build chart data with short X-axis labels
  const chartData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];

    const sorted = Object.entries(data).sort(([a], [b]) => a.localeCompare(b));
    const isLongRange = granularity === 'monthly' && sorted.length > 24;

    return sorted.map(([key, values]) => {
      let label, fullLabel;
      if (granularity === 'daily') {
        const parts = key.split('-');
        const day = parseInt(parts[2], 10);
        const mon = MONTH_NAMES[parseInt(parts[1], 10) - 1];
        label = `${day}`;
        fullLabel = `${mon} ${day}, ${parts[0]}`;
      } else {
        const [year, month] = key.split('-');
        const monthName = MONTH_NAMES[parseInt(month, 10) - 1];
        fullLabel = `${monthName} ${year}`;
        if (isLongRange) {
          label = `${monthName} '${year.slice(2)}`;
        } else {
          label = monthName;
        }
      }
      if (combined) {
        return { label, fullLabel, income: values.income || 0, expense: values.expense || 0 };
      }
      return { label, fullLabel, value: values[type] || 0 };
    });
  }, [data, type, combined, granularity]);

  const displayData = compact ? chartData.slice(-6) : chartData;

  // Compute subtitle note for context
  const subtitle = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return '';
    const keys = Object.keys(data).sort();
    const firstKey = keys[0];
    const lastKey = keys[keys.length - 1];

    if (granularity === 'daily') {
      const [fy, fm] = firstKey.split('-');
      const [ly, lm] = lastKey.split('-');
      const firstMonth = `${MONTH_NAMES[parseInt(fm, 10) - 1]} ${fy}`;
      const lastMonth = `${MONTH_NAMES[parseInt(lm, 10) - 1]} ${ly}`;
      return firstMonth === lastMonth ? firstMonth : `${firstMonth} - ${lastMonth}`;
    } else {
      const [fy] = firstKey.split('-');
      const [ly, lm] = lastKey.split('-');
      const [, fm] = firstKey.split('-');
      if (keys.length > 24) {
        return `${MONTH_NAMES[parseInt(fm, 10) - 1]} ${fy} - ${MONTH_NAMES[parseInt(lm, 10) - 1]} ${ly}`;
      }
      if (fy === ly) return fy;
      return `${fy} - ${ly}`;
    }
  }, [data, granularity]);

  const tickInterval = useMemo(() => {
    const len = displayData.length;
    if (compact) return len <= 7 ? 0 : Math.floor(len / 4);
    return 'preserveStartEnd';
  }, [displayData.length, compact]);

  if (!displayData.length) {
    return (
      <div className={`flex items-center justify-center text-surface-400 text-xs ${compact ? 'h-[100px]' : 'h-[250px]'}`}>
        No data
      </div>
    );
  }

  return (
    <div>
      {combined && (
        <div className="flex items-center gap-3 mb-1">
          <button
            onClick={() => setShowIncome(v => !v)}
            className={`flex items-center gap-1 transition-opacity ${!showIncome ? 'opacity-35' : ''}`}
          >
            <span className="w-2.5 h-0.5 rounded-full" style={{ backgroundColor: incomeColor }} />
            <span className="text-[10px] font-medium text-surface-500">Income</span>
          </button>
          <button
            onClick={() => setShowExpense(v => !v)}
            className={`flex items-center gap-1 transition-opacity ${!showExpense ? 'opacity-35' : ''}`}
          >
            <span className="w-2.5 h-0.5 rounded-full" style={{ backgroundColor: expenseColor }} />
            <span className="text-[10px] font-medium text-surface-500">Expense</span>
          </button>
        </div>
      )}
      {subtitle && (
        <p className={compact ? 'text-[9px] text-surface-400 mb-0.5' : 'text-[11px] text-surface-400 mb-1'}>{subtitle}</p>
      )}
      <div className={compact ? 'h-[120px]' : 'h-[250px]'}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={displayData} margin={compact ? { top: 4, right: 8, left: 8, bottom: 0 } : { top: 10, right: 20, left: 0, bottom: 0 }}>
            <defs>
              {combined ? (
                <>
                  <linearGradient id={incomeGradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={incomeColor} stopOpacity={0.2} />
                    <stop offset="100%" stopColor={incomeColor} stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id={expenseGradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={expenseColor} stopOpacity={0.2} />
                    <stop offset="100%" stopColor={expenseColor} stopOpacity={0.02} />
                  </linearGradient>
                </>
              ) : (
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.02} />
                </linearGradient>
              )}
            </defs>
            {!compact && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />}
            <XAxis
              dataKey="label"
              tick={{ fontSize: compact ? 9 : 12, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
              interval={tickInterval}
            />
            {!compact && (
              <YAxis
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(val) => {
                  if (val >= 100000) return `${(val / 100000).toFixed(1)}L`;
                  if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
                  return val;
                }}
                width={45}
              />
            )}
            <Tooltip content={<CustomTooltip formatAmount={formatAmount} />} />
            {combined ? (
              <>
                {showIncome && (
                  <Area
                    type="monotone"
                    dataKey="income"
                    stroke={incomeColor}
                    strokeWidth={compact ? 1.5 : 2}
                    fill={`url(#${incomeGradientId})`}
                    dot={!compact && displayData.length <= 15}
                    activeDot={{ r: compact ? 3 : 5 }}
                  />
                )}
                {showExpense && (
                  <Area
                    type="monotone"
                    dataKey="expense"
                    stroke={expenseColor}
                    strokeWidth={compact ? 1.5 : 2}
                    fill={`url(#${expenseGradientId})`}
                    dot={!compact && displayData.length <= 15}
                    activeDot={{ r: compact ? 3 : 5 }}
                  />
                )}
              </>
            ) : (
              <Area
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={compact ? 1.5 : 2}
                fill={`url(#${gradientId})`}
                dot={!compact && displayData.length <= 15}
                activeDot={{ r: compact ? 3 : 5 }}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
